import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { ChevronDown, ChevronRight, Loader2, Download, Printer, Eraser, Search, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { couplesToCSV } from "@/lib/schedule-utils";
import { useGroups } from "@/context/GroupContext";
import { useI18n } from "@/context/I18nContext";
import RemindersBanner from "@/components/RemindersBanner";
import { useColumnResize } from "@/lib/useColumnResize";

const EGG_CELLS = 6;
const CHICK_ROWS = 6;
const EMPTY_CHICK = { ring: "", hatch_date: "", photo_id: "" };

// Default widths: # | Male | Female | Egg1..6
const DEFAULT_WIDTHS = [110, 110, 90, 90, 90, 90, 90, 90];

function CoupleRowItem({ couple, index, onSave, savingIndex, onReset, widths, expanded, onToggle, checked, onCheck }) {
  const { t } = useI18n();
  const [local, setLocal] = useState(couple);
  useEffect(() => { setLocal(couple); }, [couple]);

  const dirtyRef = useRef(false);
  const setField = (patch) => {
    dirtyRef.current = true;
    setLocal((p) => ({ ...p, ...patch }));
  };
  const setEgg = (i, v) => {
    dirtyRef.current = true;
    setLocal((p) => {
      const eggs = [...(p.egg_dates || [])];
      while (eggs.length < EGG_CELLS) eggs.push("");
      eggs[i] = v;
      return { ...p, egg_dates: eggs };
    });
  };
  const setChick = (i, patch) => {
    dirtyRef.current = true;
    setLocal((p) => {
      const chicks = [...(p.chicks || [])];
      while (chicks.length < CHICK_ROWS) chicks.push({ ring: "", hatch_date: "" });
      chicks[i] = { ...chicks[i], ...patch };
      return { ...p, chicks };
    });
  };

  const onBlur = async () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    await onSave(index, {
      male_ring: local.male_ring || "",
      female_ring: local.female_ring || "",
      egg_dates: local.egg_dates || [],
      chicks: local.chicks || [],
    });
  };

  const isSaving = savingIndex === index;

  return (
    <>
      <tr data-testid={`couple-row-${index}`}>
        <td className="sel" style={{ width: 30, textAlign: "center" }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onCheck(e.target.checked)}
            style={{ width: 15, height: 15, margin: "0 auto", accentColor: "#6b7245" }}
            data-testid={`select-couple-${index}`}
          />
        </td>
        <td className="rownum">
          <button
            onClick={onToggle}
            data-testid={`expand-couple-${index}`}
            className="w-full h-full flex items-center justify-center gap-0.5"
            title={local.couple_no}
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <span>{local.couple_no}</span>
          </button>
        </td>
        <td className="male">
          <input
            value={local.male_ring || ""}
            onChange={(e) => setField({ male_ring: e.target.value })}
            onBlur={onBlur}
            placeholder="♂"
            data-testid={`male-ring-${index}`}
          />
        </td>
        <td className="female">
          <input
            value={local.female_ring || ""}
            onChange={(e) => setField({ female_ring: e.target.value })}
            onBlur={onBlur}
            placeholder="♀"
            data-testid={`female-ring-${index}`}
          />
        </td>
        {Array.from({ length: EGG_CELLS }).map((_, i) => (
          <td key={i}>
            <input
              type="date"
              value={(local.egg_dates && local.egg_dates[i]) || ""}
              onChange={(e) => setEgg(i, e.target.value)}
              onBlur={onBlur}
              data-testid={`egg-date-${index}-${i}`}
            />
          </td>
        ))}
        <td className="reset action">
          {isSaving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-espresso/40 mx-auto" />
          ) : (
            <button
              onClick={() => onReset(index)}
              className="text-espresso/30 hover:text-danger p-1"
              title={t("common.reset")}
              data-testid={`reset-couple-${index}`}
            >
              <Eraser className="w-3.5 h-3.5" />
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={11} className="xls-expand-cell">
            <div className="text-[10px] font-bold uppercase tracking-widest text-olive mb-2">
              {t("couples.chicks")} ({CHICK_ROWS})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Array.from({ length: CHICK_ROWS }).map((_, i) => {
                const chick = (local.chicks && local.chicks[i]) || { ring: "", hatch_date: "" };
                return (
                  <div
                    key={i}
                    className="flex items-center gap-1.5 bg-white border border-app-border rounded px-2 py-1"
                    data-testid={`chick-row-${index}-${i}`}
                  >
                    <span className="text-[10px] font-semibold text-espresso/50 w-12 flex-shrink-0">
                      {t("couples.chick")} {i + 1}
                    </span>
                    <input
                      value={chick.ring}
                      onChange={(e) => setChick(i, { ring: e.target.value })}
                      onBlur={onBlur}
                      placeholder={t("couples.foot_ring")}
                      className="flex-1 font-mono text-xs h-7 px-1.5 border border-app-border rounded outline-none focus:border-olive bg-white"
                      data-testid={`chick-ring-${index}-${i}`}
                    />
                    <input
                      type="date"
                      value={chick.hatch_date}
                      onChange={(e) => setChick(i, { hatch_date: e.target.value })}
                      onBlur={onBlur}
                      className="w-32 text-xs h-7 px-1.5 border border-app-border rounded outline-none focus:border-olive bg-white"
                      data-testid={`chick-hatch-${index}-${i}`}
                    />
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ResizableTh({ index, width, startResize, startResizeTouch, children, className = "" }) {
  return (
    <th style={{ width }} className={className}>
      {children}
      <span
        className="resize-handle"
        onMouseDown={(e) => startResize(e, index)}
        onTouchStart={(e) => startResizeTouch(e, index)}
      />
    </th>
  );
}

export default function CouplesPage() {
  const { activeGroup } = useGroups();
  const { t } = useI18n();
  const [couples, setCouples] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingIndex, setSavingIndex] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [expanded, setExpanded] = useState({});
  const editingRef = useRef(false);
  const { widths, startResize, startResizeTouch } = useColumnResize("xls-couples", DEFAULT_WIDTHS);

  const cacheKey = activeGroup?.id ? `couples:${activeGroup.id}` : null;

  const load = useCallback(async () => {
    if (!activeGroup?.id) return;
    try {
      const cached = cacheKey && localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed.couples)) {
          setCouples(parsed.couples);
          setUpdatedAt(parsed.couples_updated_at);
          setLoading(false);
        }
      }
    } catch (_e) { void _e; }
    try {
      const { data } = await api.get("/schedule/couples");
      setCouples(data.couples || []);
      setUpdatedAt(data.couples_updated_at);
      if (cacheKey) {
        try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch (_e) { void _e; }
      }
    } catch {
      if (!cacheKey || !localStorage.getItem(cacheKey)) {
        toast.error("Offline — no cached data yet");
      }
    } finally {
      setLoading(false);
    }
  }, [activeGroup?.id, cacheKey]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load, activeGroup?.id]);

  useEffect(() => {
    const id = setInterval(async () => {
      if (editingRef.current) return;
      try {
        const { data } = await api.get("/schedule/sync");
        if (data.couples_updated_at && data.couples_updated_at !== updatedAt) {
          const res = await api.get("/schedule/couples");
          setCouples(res.data.couples || []);
          setUpdatedAt(res.data.couples_updated_at);
        }
      } catch (_e) { void _e; }
    }, 6000);
    return () => clearInterval(id);
  }, [updatedAt]);

  const onSave = async (index, payload) => {
    setSavingIndex(index);
    editingRef.current = true;
    try {
      const { data } = await api.put(`/schedule/couples/${index}`, payload);
      setUpdatedAt(data.updated_at);
      setCouples((arr) => {
        const copy = [...arr];
        copy[index] = { ...copy[index], ...payload };
        return copy;
      });
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingIndex(null);
      setTimeout(() => { editingRef.current = false; }, 500);
    }
  };

  const doReset = async () => {
    if (resetTarget == null) return;
    const idx = resetTarget;
    setResetTarget(null);
    const empty = {
      male_ring: "",
      female_ring: "",
      egg_dates: ["", "", "", "", "", ""],
      chicks: [EMPTY_CHICK, EMPTY_CHICK, EMPTY_CHICK, EMPTY_CHICK, EMPTY_CHICK, EMPTY_CHICK],
    };
    await onSave(idx, empty);
    try {
      const { data } = await api.get("/schedule/couples");
      setCouples(data.couples || []);
      setUpdatedAt(data.couples_updated_at);
    } catch (_e) { void _e; }
    toast.success(t("common.reset"));
  };

  const toggleExpand = (i) => setExpanded((e) => ({ ...e, [i]: !e[i] }));

  const [jumpNo, setJumpNo] = useState("");
  const tableWrapRef = useRef(null);
  const [selected, setSelected] = useState({});
  const [bulkCount, setBulkCount] = useState("10");
  const selCount = Object.values(selected).filter(Boolean).length;

  const bulkAdd = async () => {
    try {
      await api.post("/schedule/couples/bulk-add", { count: parseInt(bulkCount, 10) });
      await load();
      toast.success(t("bulk.added"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteSelected = async () => {
    if (!window.confirm(t("bulk.delete_confirm"))) return;
    const indices = Object.keys(selected).filter((k) => selected[k]).map(Number);
    setSelected({});
    try {
      await api.post("/schedule/couples/delete-rows", { indices });
      await load();
      toast.success(t("bulk.deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const jumpTo = (value) => {
    setJumpNo(value);
    const n = parseInt(value, 10);
    if (!n || n < 1 || n > couples.length || !tableWrapRef.current) return;
    const row = tableWrapRef.current.querySelector(`[data-testid="couple-row-${n - 1}"]`);
    if (!row) return;
    row.scrollIntoView({ block: "center", behavior: "smooth" });
    row.style.transition = "background-color 0.3s";
    row.style.backgroundColor = "rgba(180, 190, 130, 0.35)";
    setTimeout(() => { row.style.backgroundColor = ""; }, 1600);
  };

  return (
    <div className="space-y-4" data-testid="couples-page">
      <RemindersBanner couples={couples} groupName={activeGroup?.name} incubationDays={activeGroup?.incubation_days || 21} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso">
            {t("couples.title")}
          </h1>
          <p className="text-sm text-espresso/70 mt-1">
            {activeGroup?.name ? <span className="text-olive font-semibold">{activeGroup.name}</span> : null}
            {activeGroup?.name ? " · " : ""}
            {t("couples.sub")}
          </p>
        </div>
        <div className="flex items-center gap-2" data-print-hide>
          <div className="flex items-center gap-1.5 bg-white border border-app-border rounded-lg px-2 py-1">
            <Search className="w-3.5 h-3.5 text-espresso/40" />
            <input
              type="number"
              min={1}
              max={couples.length || 300}
              value={jumpNo}
              onChange={(e) => jumpTo(e.target.value)}
              placeholder={t("couples.jump_pair")}
              className="w-24 text-sm bg-transparent outline-none placeholder:text-espresso/40"
              data-testid="couples-jump-input"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <select
              value={bulkCount}
              onChange={(e) => setBulkCount(e.target.value)}
              className="text-sm bg-white border border-app-border rounded-lg px-2 py-1.5 outline-none"
              data-testid="couples-bulk-count"
            >
              {[1, 5, 10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={bulkAdd} data-testid="couples-bulk-add">
              <Plus className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" /> {t("bulk.add")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={deleteSelected}
              disabled={selCount === 0}
              className="border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40"
              data-testid="couples-delete-rows"
            >
              <Trash2 className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" /> {t("bulk.delete")}{selCount > 0 ? ` (${selCount})` : ""}
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => couplesToCSV(couples, activeGroup?.name)}
            disabled={loading || couples.length === 0}
            data-testid="export-couples-csv"
          >
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            disabled={loading}
            data-testid="print-couples"
          >
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <div className="hidden sm:flex items-center gap-2 text-xs text-espresso/60 ml-2">
            <span>{t("common.updated")}:</span>
            <span className="font-mono">{updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</span>
          </div>
        </div>
      </div>

      <div className="xls-wrap" ref={tableWrapRef} data-testid="couples-table">
        <table className="xls">
          <thead>
            <tr>
              <th className="sel" style={{ width: 30, textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={couples.length > 0 && selCount === couples.length}
                  onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(couples.map((_, i) => [i, true])) : {})}
                  title="Select all"
                  style={{ width: 15, height: 15, margin: "0 auto", accentColor: "#6b7245" }}
                  data-testid="select-all-couples"
                />
              </th>
              <th className="rownum">#</th>
              <ResizableTh index={0} width={widths[0]} startResize={startResize} startResizeTouch={startResizeTouch}>
                {t("couples.male")}
              </ResizableTh>
              <ResizableTh index={1} width={widths[1]} startResize={startResize} startResizeTouch={startResizeTouch}>
                {t("couples.female")}
              </ResizableTh>
              {Array.from({ length: EGG_CELLS }).map((_, i) => (
                <ResizableTh key={i} index={2 + i} width={widths[2 + i]} startResize={startResize} startResizeTouch={startResizeTouch}>
                  {t("couples.egg")} {i + 1}
                </ResizableTh>
              ))}
              <th className="reset"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11} className="p-8 text-center">
                  <Loader2 className="w-5 h-5 animate-spin inline-block text-espresso/60" />
                </td>
              </tr>
            ) : (
              couples.map((c, idx) => (
                <CoupleRowItem
                  key={c.couple_no}
                  couple={c}
                  index={idx}
                  onSave={onSave}
                  savingIndex={savingIndex}
                  onReset={(i) => setResetTarget(i)}
                  widths={widths}
                  expanded={!!expanded[idx]}
                  onToggle={() => toggleExpand(idx)}
                  checked={!!selected[idx]}
                  onCheck={(v) => setSelected((s) => ({ ...s, [idx]: v }))}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={resetTarget != null} onOpenChange={(v) => !v && setResetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("couples.reset_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("couples.reset_confirm_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doReset}
              className="bg-danger hover:bg-danger/90"
              data-testid="confirm-reset-couple"
            >
              {t("common.reset")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
